import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ArtworkResponse, ArtworkFilters } from '../../services';
import { EnhancedArtworkCard } from '../gallery';
import { GalleryArtworkCardSkeleton } from '../ui';

const GridContainer = styled.div`
  width: 100%;
`;

const Grid = styled.div<{ $columns: number; $gap: string }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${({ $gap }) => $gap};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const ArtworkCell = styled.div`
  display: grid;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const PortfolioLink = styled(Link)`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `0 ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  color: ${({ theme }) => theme.colors.gallery.text.accent};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-decoration: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gallery.accent.primary};
    background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ResultsInfo = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const UpdatingMessage = styled.span`
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl} 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadMoreButton = styled.button`
  display: block;
  min-height: 44px;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.accent.border};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font: inherit;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.gallery.accent.hover};
    background: ${({ theme }) => theme.colors.gallery.surfaceElevated};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 3px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: ${({ theme }) => theme.spacing['4xl']} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const EmptyStateTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.title};
`;

const StatePanel = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  justify-items: center;
  padding: ${({ theme }) => theme.spacing['3xl']} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const StateTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.title};
`;

const StateAction = styled.button`
  min-height: 44px;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.gallery.accent.border};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  font: inherit;
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.gallery.accent.hover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 3px;
  }
`;

const EmptyStateMessage = styled.p`
  max-width: 32rem;
  margin: 0 auto;
  line-height: 1.6;
`;

const ErrorMessage = styled.p`
  max-width: 36rem;
  margin: 0;
  line-height: 1.6;
`;

export interface ArtworkGridProps {
  /** The parent owns the query and supplies its current page collection. */
  artworks: ArtworkResponse[];
  /** Authoritative server total for the current filter set. */
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => Promise<void> | void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  isFetching: boolean;
  onLoadMore: () => Promise<void> | void;
  filters?: ArtworkFilters;
  columns?: number;
  gap?: string;
  aspectRatio?: number;
  pageSize?: number;
  onArtworkClick?: (artwork: ArtworkResponse, activatingElement?: HTMLButtonElement) => void;
  galleryMode?: boolean;
  className?: string;
  onClearFilters?: () => void;
  showPortfolioLinks?: boolean;
  onPortfolioNavigate?: (artwork: ArtworkResponse) => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'We could not load the artwork right now. Please try again.';
}

export function ArtworkGrid({
  artworks,
  totalCount,
  isLoading,
  isError,
  error,
  onRetry,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  isFetching,
  onLoadMore,
  filters = {},
  columns = 4,
  gap = '24px',
  aspectRatio = 3 / 4,
  pageSize = 20,
  onArtworkClick,
  className,
  onClearFilters,
  showPortfolioLinks = false,
  onPortfolioNavigate,
}: ArtworkGridProps) {
  const hasFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '',
  );

  if (isLoading && artworks.length === 0) {
    return (
      <GridContainer className={className} aria-busy="true" aria-label="Loading artworks">
        <StatusBar><ResultsInfo>Loading artworks…</ResultsInfo></StatusBar>
        <Grid $columns={columns} $gap={gap}>
          {Array.from({ length: Math.min(pageSize, 20) }, (_, index) => (
            <GalleryArtworkCardSkeleton key={index} aspectRatio={aspectRatio} />
          ))}
        </Grid>
      </GridContainer>
    );
  }

  if (isError && artworks.length === 0) {
    return (
      <GridContainer className={className} role="alert">
        <StatePanel>
          <StateTitle>Gallery unavailable</StateTitle>
          <ErrorMessage>{errorMessage(error)}</ErrorMessage>
          <StateAction type="button" onClick={() => void onRetry()}>
            Try again
          </StateAction>
        </StatePanel>
      </GridContainer>
    );
  }

  if (artworks.length === 0) {
    return (
      <GridContainer className={className}>
        <EmptyState>
          <EmptyStateTitle>{hasFilters ? 'No matching artworks' : 'No artworks yet'}</EmptyStateTitle>
          <EmptyStateMessage>
            {hasFilters
              ? 'No artworks match those filters. Try a different search or clear the filters.'
              : 'There are no public artworks to display yet.'}
          </EmptyStateMessage>
          {hasFilters && onClearFilters && (
            <StateAction type="button" onClick={onClearFilters}>Clear filters</StateAction>
          )}
        </EmptyState>
      </GridContainer>
    );
  }

  return (
    <GridContainer className={className}>
      <StatusBar>
        <ResultsInfo aria-live="polite">Showing {artworks.length} of {totalCount} artworks</ResultsInfo>
        {isFetching && !isFetchingNextPage && <UpdatingMessage>Updating…</UpdatingMessage>}
      </StatusBar>

      <Grid $columns={columns} $gap={gap}>
        {artworks.map((artwork) => (
          <ArtworkCell key={artwork.artwork_id}>
            <EnhancedArtworkCard
              artwork={artwork}
              aspectRatio={aspectRatio}
              onClick={onArtworkClick}
            />
            {showPortfolioLinks && artwork.student_id && (
              <PortfolioLink
                to={`/artists/${artwork.student_id}`}
                onClick={() => onPortfolioNavigate?.(artwork)}
              >
                View portfolio
              </PortfolioLink>
            )}
          </ArtworkCell>
        ))}
      </Grid>

      {hasNextPage && (
        <>
          {isFetchingNextPage && <LoadingMore aria-live="polite">Loading more artworks…</LoadingMore>}
          <LoadMoreButton type="button" onClick={() => void onLoadMore()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading more artworks…' : 'Load more artworks'}
          </LoadMoreButton>
        </>
      )}

      {isError && artworks.length > 0 && (
        <StatePanel role="alert">
          <StateTitle>{isFetchNextPageError ? 'More artworks are unavailable' : 'Gallery update unavailable'}</StateTitle>
          <ErrorMessage>{errorMessage(error)}</ErrorMessage>
          <StateAction
            type="button"
            onClick={() => void (isFetchNextPageError ? onLoadMore() : onRetry())}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Trying again…' : 'Try again'}
          </StateAction>
        </StatePanel>
      )}
    </GridContainer>
  );
}
